import { type JsonLogic, QueryBuilderFieldGroup } from '@dotfile/shared/domain';

function isObject(value: unknown): value is object {
  return !!value && !Array.isArray(value) && typeof value === 'object';
}

function walkJsonLogic(
  jsonLogic: JsonLogic,
  availableFieldVars: string[],
): JsonLogic {
  if (!jsonLogic) return jsonLogic;

  return Object.keys(jsonLogic).reduce((acc, key) => {
    const value = jsonLogic[key as keyof typeof jsonLogic];

    if (isObject(value)) {
      // go deeper
      return { [key]: walkJsonLogic(value, availableFieldVars) };
    }

    if (Array.isArray(value)) {
      const values = value as object[];
      const hasInvalidVariables = values.find(
        (v) =>
          isObject(v) &&
          'var' in v &&
          typeof v['var'] === 'string' &&
          !availableFieldVars.includes(v['var']),
      );

      // skip whole condition on any invalid variables
      if (hasInvalidVariables) {
        return acc;
      }

      const cleanValues = values.map((v) => {
        if (isObject(v))
          // go deeper
          return walkJsonLogic(v as JsonLogic, availableFieldVars);
        return v;
      });

      // Skip if empty conditions (removed because invalid)
      if (
        !cleanValues.length ||
        cleanValues.every((v) => isObject(v) && Object.keys(v).length === 0)
      ) {
        return acc;
      }

      return {
        ...acc,
        [key]: cleanValues.filter((v) =>
          // Remove empty object
          isObject(v) ? Object.keys(v).length : true,
        ),
      };
    }

    return { ...acc, [key]: value };
  }, {});
}

/**
 * Clean jsonLogic by removing invalid condition (unresolved variables) based on available fields
 * @param fields
 * @param jsonLogic
 * @returns jsonLogic without invalid condition
 */
export function removeExtraneousJsonLogic(
  fields: QueryBuilderFieldGroup[],
  jsonLogic: JsonLogic,
): JsonLogic {
  const availableFieldVars = Object.values(fields).flatMap((field) =>
    field.options.flatMap((option) => option.name),
  );
  return walkJsonLogic(jsonLogic, availableFieldVars);
}
